<template>
  <div>
    <b-container fluid class="pb-4 px-lg-4">
      <div class="addl-header">
        <div>
          <h3 class="addl-title">Servicios Adicionales</h3>
          <p class="addl-subtitle">
            Complementa tu plan con servicios extra para tu negocio
          </p>
        </div>
      </div>

      <!-- Módulo de marketing / Saldo de WhatsApp / Créditos de IA — un solo
           grid compartido para que las cards se acomoden 2 por fila en
           pantallas anchas en vez de apilarse una sola columna angosta con
           medio viewport vacío a la derecha. Módulo y Saldo son mutuamente
           excluyentes (según marketingAddonActive), así que nunca compiten
           por espacio entre sí, solo con Créditos de IA. -->
      <div
        v-if="marketingAddonPlan || aiCreditPacks.length"
        class="addl-grid addl-grid--addons mb-3"
      >
        <!-- Módulo de marketing (WhatsApp) — activable solo (sin cambiar de
             plan) o, alternativamente, marcando la casilla al comprar/mejorar
             un plan en Planes para que quede en una sola factura. -->
        <div
          v-if="marketingAddonPlan && !marketingAddonActive"
          class="addl-card addl-card--wide"
        >
          <div class="addl-card-top">
            <div class="addl-card-icon">
              <i class="el-icon-chat-dot-round"></i>
            </div>
            <div class="addl-card-info">
              <h4 class="addl-card-name">Módulo de marketing (WhatsApp)</h4>
              <p class="addl-card-legend">
                Conversaciones, bots y automatizaciones
              </p>
            </div>
          </div>
          <p class="addl-card-desc">
            Actívalo aquí sin cambiar tu plan actual. Incluye ${{
              marketingAddonPlan.welcome_gift_usd
            }}
            de saldo de bienvenida.
          </p>

          <div class="addl-period-toggle">
            <button
              type="button"
              class="addl-period-btn"
              :class="{
                'addl-period-btn--active': addonBillingPeriod === 'monthly'
              }"
              @click="addonBillingPeriod = 'monthly'"
            >
              Mensual
            </button>
            <button
              type="button"
              class="addl-period-btn"
              :class="{
                'addl-period-btn--active': addonBillingPeriod === 'annual'
              }"
              @click="addonBillingPeriod = 'annual'"
            >
              Anual
            </button>
          </div>

          <div class="addl-card-bottom">
            <div class="addl-card-price">
              <span class="addl-price-amount">${{ addonSelectedPrice }}</span>
              <span class="addl-price-tax">
                / {{ addonBillingPeriod === "annual" ? "año" : "mes" }} + IVA
              </span>
            </div>
            <el-button
              type="primary"
              size="small"
              class="addl-pay-btn"
              :loading="buyingMarketingAddon"
              @click="buyMarketingAddon"
            >
              <i class="el-icon-shopping-cart-2"></i> Activar módulo
            </el-button>
          </div>
        </div>

        <!-- Saldo de WhatsApp — solo con el módulo activo. Slider de tokens
             en vez de un input libre: se ve el precio y la equivalencia en
             mensajes en vivo mientras se arrastra (pedido explícito 2026-08). -->
        <div v-if="marketingAddonActive" class="addl-card addl-card--wide">
          <div class="addl-card-top">
            <div class="addl-card-icon">
              <i class="el-icon-chat-dot-round"></i>
            </div>
            <div class="addl-card-info">
              <h4 class="addl-card-name">Saldo de WhatsApp</h4>
              <p class="addl-card-legend">
                1 token = $0.01 · recarga mínima 1,000 tokens ($10)
              </p>
            </div>
          </div>

          <div class="addl-slider-wrap">
            <el-slider
              v-model="waTierIndex"
              :min="0"
              :max="waTiers.length - 1"
              :step="1"
              :show-tooltip="false"
              :marks="waSliderMarks"
            ></el-slider>
          </div>

          <div class="addl-equiv">
            <div class="addl-equiv-price">
              <span class="addl-price-amount">${{ waSelectedTier.usd }}</span>
              <span class="addl-price-tax">
                ({{ waSelectedTier.tokens.toLocaleString("es-EC") }} tokens +
                IVA)
              </span>
            </div>
            <p class="addl-equiv-msgs">
              ≈ {{ waSelectedEquivalence.marketing }} mensajes de Marketing ·
              {{ waSelectedEquivalence.session }} de Sesión/Utilidad
            </p>
          </div>

          <div class="addl-card-bottom addl-card-bottom--single">
            <el-button
              type="primary"
              size="small"
              class="addl-pay-btn"
              :loading="buyingWaTopup"
              @click="buyWaTopup"
            >
              <i class="el-icon-shopping-cart-2"></i> Recargar saldo
            </el-button>
          </div>

          <label v-if="waStatus.has_card_on_file" class="addl-autorecharge">
            <el-switch
              v-model="waAutoRecharge"
              active-color="#3fdbbf"
              @change="toggleWaAutoRecharge"
            ></el-switch>
            Recargar automáticamente ${{ waAutoRechargeAmount }} cuando el saldo
            llegue a $1 (usa tu tarjeta guardada)
          </label>
          <p v-else class="addl-autorecharge addl-autorecharge--hint">
            <i class="el-icon-info"></i> Guarda una tarjeta pagando aquí o tu
            plan con PayPhone para poder activar la recarga automática.
          </p>
        </div>

        <!-- Créditos de IA — un solo slider entre los packs vigentes (server-
             driven, viene de ai_credit_packs). El ícono ⓘ explica en qué se
             gastan: bot de WhatsApp, copiloto, creador de flujos y landing pages. -->
        <div v-if="aiCreditPacks.length" class="addl-card addl-card--wide">
          <div class="addl-card-top">
            <div class="addl-card-icon">
              <i class="el-icon-magic-stick"></i>
            </div>
            <div class="addl-card-info">
              <h4 class="addl-card-name">
                Créditos de IA
                <el-tooltip placement="top" popper-class="addl-ai-tooltip">
                  <div slot="content">
                    <b>1 crédito = 1 uso de IA.</b> Se gastan en:
                    <ul class="addl-ai-tooltip-list">
                      <li>Respuestas del bot de WhatsApp</li>
                      <li>Chat del copiloto en el CRM</li>
                      <li>Creación de flujos por chat ("vibe coding")</li>
                      <li>Generación de landing pages con IA</li>
                    </ul>
                    Se acreditan solo para el mes en curso, igual que tu cupo
                    mensual del plan.
                  </div>
                  <i class="el-icon-info addl-info-icon"></i>
                </el-tooltip>
              </h4>
              <p class="addl-card-legend">
                Adicionales a tu cupo mensual. Se agotan al fin de mes, igual
                que el cupo del plan
              </p>
            </div>
          </div>

          <div class="addl-slider-wrap">
            <el-slider
              v-model="aiTierIndex"
              :min="0"
              :max="aiCreditPacks.length - 1"
              :step="1"
              :show-tooltip="false"
              :marks="aiSliderMarks"
            ></el-slider>
          </div>

          <div class="addl-equiv">
            <div class="addl-equiv-price">
              <span class="addl-price-amount"
                >${{ selectedAiPack.price_usd }}</span
              >
              <span class="addl-price-tax">
                ({{ selectedAiPack.credits }} créditos + IVA)
              </span>
            </div>
          </div>

          <div class="addl-card-bottom addl-card-bottom--single">
            <el-button
              type="primary"
              size="small"
              class="addl-pay-btn"
              :loading="buyingAiPackSlug === selectedAiPack.slug"
              @click="buyAiPack(selectedAiPack)"
            >
              <i class="el-icon-shopping-cart-2"></i> Comprar
            </el-button>
          </div>
        </div>
      </div>

      <template v-if="apiResponse.success">
        <div v-if="items.length" class="addl-grid">
          <div v-for="(item, index) in items" :key="index" class="addl-card">
            <div class="addl-card-top">
              <div class="addl-card-icon">
                <i class="el-icon-document-checked"></i>
              </div>
              <div class="addl-card-info">
                <h4 class="addl-card-name">{{ item.name }}</h4>
                <p v-if="item.legend" class="addl-card-legend">
                  {{ item.legend }}
                </p>
              </div>
            </div>

            <p v-if="item.description" class="addl-card-desc">
              {{ item.description }}
            </p>

            <div class="addl-card-bottom">
              <div class="addl-card-price">
                <span class="addl-price-amount">${{ item.amount }}</span>
                <span class="addl-price-tax">+ IVA</span>
              </div>
              <el-button
                type="primary"
                size="small"
                class="addl-pay-btn"
                @click="payAdditional(item)"
              >
                <i class="el-icon-shopping-cart-2"></i> Adquirir
              </el-button>
            </div>
          </div>
        </div>

        <div
          v-else-if="!marketingAddonPlan && !aiCreditPacks.length"
          class="addl-empty"
        >
          <i class="el-icon-box"></i>
          <span>No hay servicios adicionales disponibles</span>
        </div>
      </template>

      <div v-else class="addl-loading">
        <i class="el-icon-loading"></i> Cargando servicios...
      </div>
    </b-container>
  </div>
</template>

<script>
import payService from "../services/payService";

// Tarifas de venta vigentes (Ecuador, Gupshup) — deben coincidir con
// wa_rate_cards en Back-mkt (ver migración 20260818010000_wa_rate_cards_margin_v2).
// Es solo para mostrar una equivalencia aproximada; el cobro real siempre lo
// decide la tarifa vigente en Back-mkt al momento de enviar el mensaje.
const WA_SALE_PRICE_MARKETING = 0.101;
const WA_SALE_PRICE_SESSION = 0.02;

export default {
  name: "AdditionalsComponent",
  data: () => ({
    apiResponse: { success: false },
    items: [],
    marketingAddonPlan: null,
    marketingAddonActive: false,
    buyingMarketingAddon: false,
    addonBillingPeriod: "monthly",
    aiCreditPacks: [],
    buyingAiPackSlug: null,

    // Saldo de WhatsApp (slider de tokens)
    waTiers: [
      { tokens: 1000, usd: 10 },
      { tokens: 2500, usd: 25 },
      { tokens: 5000, usd: 50 },
      { tokens: 10000, usd: 100 }
    ],
    waTierIndex: 0,
    buyingWaTopup: false,
    waStatus: { has_card_on_file: false, auto_recharge: false },
    waAutoRecharge: false,
    waAutoRechargeAmount: 10,

    // Créditos de IA (slider sobre los packs vigentes)
    aiTierIndex: 0
  }),
  computed: {
    waSliderMarks() {
      const marks = {};
      this.waTiers.forEach((tier, i) => {
        marks[i] = `$${tier.usd}`;
      });
      return marks;
    },
    waSelectedTier() {
      return this.waTiers[this.waTierIndex] || this.waTiers[0];
    },
    waSelectedEquivalence() {
      const usd = this.waSelectedTier.usd;
      return {
        marketing: Math.floor(usd / WA_SALE_PRICE_MARKETING),
        session: Math.floor(usd / WA_SALE_PRICE_SESSION)
      };
    },
    aiSliderMarks() {
      const marks = {};
      this.aiCreditPacks.forEach((pack, i) => {
        marks[i] = `${pack.credits}`;
      });
      return marks;
    },
    selectedAiPack() {
      return (
        this.aiCreditPacks[this.aiTierIndex] || this.aiCreditPacks[0] || {}
      );
    },
    addonSelectedPrice() {
      if (!this.marketingAddonPlan) return 0;
      return this.addonBillingPeriod === "annual"
        ? this.marketingAddonPlan.standalone_price_annual
        : this.marketingAddonPlan.standalone_price_monthly;
    }
  },
  methods: {
    async payAdditional(item) {
      try {
        this.$store.dispatch("toggleRequestLoading", {
          text: "Por favor espere un momento, estamos validando la transacción."
        });
        const datos = { additional_id: item.id };
        let response = await payService.postPayAdditional(datos).finally(() => {
          this.$store.dispatch("toggleRequestLoading");
        });
        const { success, data: checkOutPayphone } = response.data;
        if (success) {
          if (checkOutPayphone) {
            location.href = checkOutPayphone;
          } else {
            this.$notifications.error({});
          }
        }
      } catch (error) {
        return false;
      }
    },
    async getAdditionals() {
      try {
        let response = await payService.getPayAdditionals();
        const { success, data } = response.data;
        if (success) {
          this.items = data;
          this.apiResponse.success = true;
        }
      } catch (error) {
        return false;
      }
    },
    async loadMarketingAddon() {
      try {
        const [{ data: planRes }, { data: statusRes }] = await Promise.all([
          payService.getWhatsappAddonPlan(),
          payService.getWhatsappAddonStatus()
        ]);
        if (planRes && planRes.success) this.marketingAddonPlan = planRes.data;
        if (statusRes && statusRes.success && statusRes.data) {
          this.marketingAddonActive = !!statusRes.data.active;
          this.waStatus = statusRes.data;
          this.waAutoRecharge = !!statusRes.data.auto_recharge;
        }
      } catch (e) {
        // silencioso a propósito
      }
    },
    buyWaTopup() {
      this.buyingWaTopup = true;
      payService
        .topupWhatsappWalletPp({ amount_usd: this.waSelectedTier.usd })
        .then(response => {
          const { success, data: checkOutPayphone } = response.data;
          if (success && checkOutPayphone) {
            location.href = checkOutPayphone;
          } else {
            this.$notifications.error({
              message: "No se pudo iniciar la recarga de saldo"
            });
          }
        })
        .catch(() => {
          this.$notifications.error({
            message: "No se pudo iniciar la recarga de saldo"
          });
        })
        .finally(() => {
          this.buyingWaTopup = false;
        });
    },
    toggleWaAutoRecharge(enabled) {
      payService
        .setWhatsappAutoRecharge({ enabled })
        .then(({ data }) => {
          if (!data || !data.success) {
            this.waAutoRecharge = !enabled;
            this.$notifications.error({
              message:
                (data && data.message) ||
                "No se pudo cambiar la recarga automática"
            });
          }
        })
        .catch(() => {
          this.waAutoRecharge = !enabled;
          this.$notifications.error({
            message: "No se pudo cambiar la recarga automática"
          });
        });
    },
    buyMarketingAddon() {
      this.buyingMarketingAddon = true;
      payService
        .activateWhatsappAddonPp({ billing_period: this.addonBillingPeriod })
        .then(response => {
          const { success, data: checkOutPayphone } = response.data;
          if (success && checkOutPayphone) {
            location.href = checkOutPayphone;
          } else {
            this.$notifications.error({
              message: "No se pudo iniciar la activación del módulo"
            });
          }
        })
        .catch(() => {
          this.$notifications.error({
            message: "No se pudo iniciar la activación del módulo"
          });
        })
        .finally(() => {
          this.buyingMarketingAddon = false;
        });
    },
    async loadAiCreditPacks() {
      try {
        const { data } = await payService.getAiCreditPacks();
        if (data && data.success) this.aiCreditPacks = data.data;
      } catch (e) {
        // silencioso a propósito
      }
    },
    buyAiPack(pack) {
      this.buyingAiPackSlug = pack.slug;
      payService
        .payAiCreditPackPp({ pack_slug: pack.slug })
        .then(response => {
          const { success, data: checkOutPayphone } = response.data;
          if (success && checkOutPayphone) {
            location.href = checkOutPayphone;
          } else {
            this.$notifications.error({
              message: "No se pudo iniciar la compra del pack"
            });
          }
        })
        .catch(() => {
          this.$notifications.error({
            message: "No se pudo iniciar la compra del pack"
          });
        })
        .finally(() => {
          this.buyingAiPackSlug = null;
        });
    }
  },
  mounted() {
    this.getAdditionals();
    this.loadMarketingAddon();
    this.loadAiCreditPacks();
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.addl-header {
  margin-bottom: 24px;
}
.addl-title {
  font-size: 20px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}
.addl-subtitle {
  font-size: 14px;
  color: #909399;
  margin: 4px 0 0;
}

.addl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.addl-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: all 0.15s;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border-color: darken($border, 8%);
  }
}
.addl-card-top {
  display: flex;
  align-items: center;
  gap: 14px;
}
.addl-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #ece8ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 22px;
    color: #534ab7;
  }
}
.addl-card-info {
  flex: 1;
  min-width: 0;
}
.addl-card-name {
  font-size: 17px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}
.addl-card-legend {
  font-size: 13px;
  color: #909399;
  margin: 2px 0 0;
}
.addl-card-desc {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  margin: 0;
}

.addl-card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid $border;
}
.addl-card-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.addl-price-amount {
  font-size: 28px;
  font-weight: 800;
  color: $primary;
}
.addl-price-tax {
  font-size: 12px;
  color: #909399;
  font-weight: 600;
}
.addl-pay-btn {
  background: $accent !important;
  border-color: $accent !important;
  color: #fff !important;
  border-radius: 10px;
  font-size: 14px;
  padding: 10px 20px;

  &:hover {
    opacity: 0.9;
  }
}

.addl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  color: #c0c4cc;
  gap: 10px;

  i {
    font-size: 36px;
  }

  span {
    font-size: 14px;
  }
}
.addl-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #909399;
  font-size: 14px;
  gap: 8px;
}

.addl-grid--addons {
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
}
.addl-card--wide {
  max-width: 560px;
}
.addl-period-toggle {
  display: flex;
  gap: 8px;
}
.addl-period-btn {
  flex: 1;
  padding: 8px 0;
  border-radius: 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: $primary;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &--active {
    border-color: $accent;
    background: rgba(63, 219, 191, 0.08);
    color: $accent;
  }
}
.addl-card-bottom--single {
  justify-content: flex-end;
}
.addl-pay-btn--link {
  display: inline-flex;
  align-items: center;
  background: $accent !important;
  border-color: $accent !important;
  color: #fff !important;
  border-radius: 10px;
  font-size: 14px;
  padding: 8px 18px;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }
}

.addl-slider-wrap {
  padding: 4px 8px 28px;
}
.addl-equiv {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.addl-equiv-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.addl-equiv-msgs {
  font-size: 12.5px;
  color: #606266;
  margin: 0;
}

.addl-autorecharge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  color: #606266;
  padding-top: 12px;
  border-top: 1px solid $border;
  cursor: pointer;
}
.addl-autorecharge--hint {
  cursor: default;
  color: #909399;

  i {
    margin-right: 2px;
  }
}

.addl-info-icon {
  font-size: 14px;
  color: #909399;
  margin-left: 6px;
  cursor: help;
  vertical-align: middle;
}
</style>

<style>
.addl-ai-tooltip {
  max-width: 260px;
}
.addl-ai-tooltip-list {
  margin: 6px 0;
  padding-left: 18px;
}
</style>
